import {FC} from 'react';

import {Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import classNames from 'classnames';

enum CollectionType {
  NONE = 'NONE',
  LOYALTY = 'LOYALTY',
  PROMOTION = 'PROMOTION',
  IMAGE_LEFT_RIBBON = 'IMAGE_LEFT_RIBBON',
  IMAGE_RIGHT_RIBBON = 'IMAGE_RIGHT_RIBBON',
  PROPOINT_PROMOTION = 'PROPOINT_PROMOTION',
  COMPLEX_PROMOTION = 'COMPLEX_PROMOTION',
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  COUNTDOWN_TIMER = 'COUNTDOWN_TIMER'
}

type MarketingBadgeProps = {
  text: string;
  type: CollectionType;
  accessibilityId: string;
  isPDP?: boolean;
};

const BadgeWrapper = styled('div')(({theme}) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  borderRadius: theme.spacing(0.625),
  maxWidth: theme.spacing(20),
  padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
  '&.primary': {
    backgroundColor: (theme.palette.common as any).mds_global_color_red_25
  },
  '&.secondary': {
    backgroundColor: (theme.palette.common as any).white,
    border: `1px solid ${(theme.palette.common as any).mds_global_color_red_25}`
  },
  '&.loyalty': {
    backgroundColor: (theme.palette.common as any).mds_global_color_yellow_3
  },
  '&.pdp': {
    marginBottom: theme.spacing(1),
    position: 'relative',
    width: 'fit-content',
    top: 0,
    left: 0
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: theme.spacing(15)
  }
}));

const BadgeText = styled(Typography)(({theme}) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.spacing(1.5),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '1',
  '&.primary': {
    color: (theme.palette.common as any).white
  },
  '&.secondary': {
    color: (theme.palette.common as any).mds_global_color_red_25
  },
  '&.loyalty': {
    color: (theme.palette.common as any).black
  }
}));

const MarketingBadge: FC<MarketingBadgeProps> = ({text, type, accessibilityId, isPDP}) => {
  return (
    <BadgeWrapper
      className={classNames({
        primary: type.toUpperCase() === CollectionType.PRIMARY,
        secondary: type.toUpperCase() === CollectionType.SECONDARY,
        loyalty: type.toUpperCase() === CollectionType.LOYALTY,
        pdp: isPDP
      })}
      aria-label={text}
      data-test-id={accessibilityId}
    >
      <BadgeText
        className={classNames({
          primary: type.toUpperCase() === CollectionType.PRIMARY,
          secondary: type.toUpperCase() === CollectionType.SECONDARY,
          loyalty: type.toUpperCase() === CollectionType.LOYALTY
        })}
      >
        {text}
      </BadgeText>
    </BadgeWrapper>
  );
};

export default MarketingBadge;
